// src/pages/auth/Register.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(0|\+212)([ \-_/]*)(6|7)([0-9]{8})$/;

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'user'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setErrors(prev => ({ ...prev, server: undefined }));
  }, [formData.firstName, formData.lastName, formData.email, formData.phone, formData.password, formData.confirmPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const v = {};
    if (!formData.firstName.trim()) v.firstName = 'Prénom requis';
    if (!formData.lastName.trim()) v.lastName = 'Nom requis';
    if (!formData.email || !EMAIL_REGEX.test(formData.email)) v.email = 'Email invalide';
    
    // ✅ TÉLÉPHONE OBLIGATOIRE
    if (!formData.phone) {
      v.phone = 'Numéro de téléphone requis pour la livraison';
    } else if (!PHONE_REGEX.test(formData.phone)) {
      v.phone = 'Téléphone invalide (ex: 0612345678)';
    }
    
    if (!formData.password || formData.password.length < 6) v.password = 'Mot de passe (6+ caractères)';
    if (formData.password !== formData.confirmPassword) v.confirmPassword = 'Les mots de passe ne correspondent pas';
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const { confirmPassword, ...payload } = formData;
      const data = await register(payload);

      toast.success('Inscription réussie !');

      const role = data?.user?.role || data?.role || (data?.data && data.data.user && data.data.user.role);

      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (role === 'merchant') {
        navigate('/merchant/dashboard');
      } else {
        navigate('/dashboard/profile'); // ✅ Vers profil
      }
    } catch (err) {
      const apiErr = err?.response?.data || {};
      const fieldErrors = apiErr?.errors || apiErr?.fields || {};
      const nextErrors = {};

      if (fieldErrors.email) nextErrors.email = fieldErrors.email;
      if (fieldErrors.password) nextErrors.password = fieldErrors.password;
      if (fieldErrors.firstName) nextErrors.firstName = fieldErrors.firstName;
      if (fieldErrors.lastName) nextErrors.lastName = fieldErrors.lastName;
      if (fieldErrors.phone) nextErrors.phone = fieldErrors.phone;

      const serverMessage = apiErr?.message || apiErr?.error || err?.message || 'Erreur lors de l\'inscription';

      setErrors(prev => ({ ...prev, ...nextErrors, server: serverMessage }));
      toast.error(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h2 className="text-3xl font-bold text-center mb-6">Inscription</h2>

        {errors.server && (
          <div role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 p-3 rounded">
            {errors.server}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">
                Prénom <span className="text-red-500">*</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                required
                aria-invalid={!!errors.firstName}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.firstName ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.firstName && <p id="firstName-error" className="mt-2 text-xs text-red-600">{errors.firstName}</p>}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                required
                aria-invalid={!!errors.lastName}
                aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.lastName ? 'border-red-300' : 'border-gray-300'}`}
              />
              {errors.lastName && <p id="lastName-error" className="mt-2 text-xs text-red-600">{errors.lastName}</p>}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`}
            />
            {errors.email && <p id="email-error" className="mt-2 text-xs text-red-600">{errors.email}</p>}
          </div>

          {/* ✅ TÉLÉPHONE OBLIGATOIRE */}
          <div className="mb-4">
            <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
              Téléphone <span className="text-red-500">*</span>
              <span className="text-xs font-normal text-gray-500 ml-2">(pour la livraison)</span>
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="0612345678"
              required
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? 'phone-error' : undefined}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.phone ? 'border-red-300' : 'border-gray-300'}`}
            />
            {errors.phone && <p id="phone-error" className="mt-2 text-xs text-red-600">{errors.phone}</p>}
            <p className="mt-1 text-xs text-gray-500">Format: 06XXXXXXXX ou 07XXXXXXXX</p>
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
              Mot de passe <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600 px-2 py-1"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            {errors.password && <p id="password-error" className="mt-2 text-xs text-red-600">{errors.password}</p>}
            <p className="mt-2 text-xs text-gray-500">Minimum 6 caractères</p>
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-gray-700 font-medium mb-2">
              Confirmer le mot de passe <span className="text-red-500">*</span>
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              aria-invalid={!!errors.confirmPassword}
              aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`}
            />
            {errors.confirmPassword && <p id="confirmPassword-error" className="mt-2 text-xs text-red-600">{errors.confirmPassword}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center"
            aria-busy={loading}
          >
            {loading ? 'Inscription…' : 'S\'inscrire'}
          </button>
        </form>

        <div className="mt-6 text-center text-gray-600">
          Vous avez déjà un compte ?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-semibold">
            Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;